import React, { ReactElement, useMemo, useState } from 'react'
import LegacyModal from '../Common/Components/Modal'
import { Modal as RouterModal } from '../../ReactRouting/Components/Molecules/Modal/Modal'
import BulkInsertWizard from './BulkInsertWizard'
import { LanguageContextProvider, MessageContextProvider, PromptContextProvider } from '../Common/Contexts'
import { LanguageBulkInsertUsers } from '../../../i18n'
import CLanguageHandler from '../../NonReact/Languages/LanguageHandler'
import { DocTopic } from '../Common/Constants/Documentation'
import { ServletPathContextProvider } from '../Common/Contexts/ServletPathContext'
import { clearModalHelpTopic, setModalHelpTopicID } from '../Common/Utils/HelpPortal'
import { InlinePromptProps } from '../Common/Components/InlinePrompt/InlinePrompt'

type Props = {
    servletPath: string
    refreshAction: () => void
    /** Invoked after the modal finishes its close transition (parent can unmount this component). */
    onWizardExited?: () => void
    /** When true, use the routed admin modal shell, router exit prompt, and wizard styling for list pages. */
    isRouterMode?: boolean
}

/**
 * Bulk insert users modal component.
 *
 * @param servletPath The servlet path.
 * @param refreshAction Refresh action callback to run on successful bulk insert.
 * @param onWizardExited Optional callback after the modal close animation (for parent unmount).
 * @param isRouterMode When true, apply router modal and wizard presentation (dark mode compatible).
 */
const BulkInsertUsers: React.FC<Props> = ({
    servletPath,
    refreshAction,
    onWizardExited,
    isRouterMode = false
}): ReactElement => {
    const [isModalOpen, setModalOpen] = useState<boolean>(true)
    const language: CLanguageHandler = CLanguageHandler.getInstance()
    language.loadBundle(LanguageBulkInsertUsers.NAME)

    const inlineExitPromptSettings: InlinePromptProps = useMemo(
        (): InlinePromptProps => ({
            message: language.get(LanguageBulkInsertUsers.LanguageBulkInsertUsers_PromptdefaultcloseMessage),
            confirmButtonText: language.get(LanguageBulkInsertUsers.LanguageBulkInsertUsers_PromptdefaultcloseConfirm),
            cancelButtonText: language.get(LanguageBulkInsertUsers.LanguageBulkInsertUsers_PromptdefaultcloseCancel),
            ...(isRouterMode ? { isRouterMode: true } : {})
        }),
        [isRouterMode, language]
    )

    const modalBody: ReactElement = isModalOpen ? (
        <LanguageContextProvider>
            <ServletPathContextProvider value={servletPath}>
                <PromptContextProvider isRouterMode={isRouterMode}>
                    <MessageContextProvider>
                        <div
                            className={
                                isRouterMode ? 'bulk-insert-users bulk-insert-users--router-mode' : 'bulk-insert-users'
                            }
                        >
                            <BulkInsertWizard
                                refreshAction={refreshAction}
                                isRouterMode={isRouterMode}
                                close={(): void => {
                                    setModalOpen(false)
                                }}
                            />
                        </div>
                    </MessageContextProvider>
                </PromptContextProvider>
            </ServletPathContextProvider>
        </LanguageContextProvider>
    ) : (
        <></>
    )

    const handleExited = (): void => {
        clearModalHelpTopic()
        onWizardExited?.()
    }

    return (
        <div>
            {isRouterMode ? (
                <RouterModal
                    isOpen={isModalOpen}
                    closeOnBackdropClick={true}
                    isClosableWithXButton={true}
                    className='bulk-csv-wizard-dialog'
                    inlineExitPromptSettings={inlineExitPromptSettings}
                    onOpen={(): void => {
                        setModalHelpTopicID(DocTopic.ADMINAPPUSERSBULK)
                    }}
                    onClose={(): void => {
                        setModalOpen(false)
                    }}
                    onExited={handleExited}
                >
                    {modalBody}
                </RouterModal>
            ) : (
                <LegacyModal
                    isOpen={isModalOpen}
                    closeOnBackdropClick={true}
                    isClosableWithXButton={true}
                    inlineExitPromptSettings={inlineExitPromptSettings}
                    onOpen={(): void => {
                        setModalHelpTopicID(DocTopic.ADMINAPPUSERSBULK)
                    }}
                    onClose={(): void => {
                        setModalOpen(false)
                    }}
                    onExited={handleExited}
                >
                    {modalBody}
                </LegacyModal>
            )}
        </div>
    )
}

export default BulkInsertUsers
